import { useState, useCallback, useEffect } from 'react';
import { useAuth } from './useAuth';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc } from 'firebase/firestore';

/**
 * Shared hook for fetching/saving departments from Firestore.
 * Used by DepartmentsPage (CRUD) and StepEmployment (dropdown).
 *
 * Firestore path: companies/{companyId}/settings/departments
 * Document shape: { items: [{ id, name, createdAt, updatedAt }] }
 */
const useDepartments = () => {
  const { company } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const docRef = company ? doc(db, 'companies', company.id, 'settings', 'departments') : null;

  const fetchDepartments = useCallback(async () => {
    if (!docRef) return;
    try {
      setLoading(true);
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        setItems(snap.data().items || []);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('[useDepartments] fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [docRef]);

  useEffect(() => {
    if (company) fetchDepartments();
  }, [company]);

  const saveDepartments = async (updatedItems) => {
    if (!docRef) return;
    try {
      await setDoc(docRef, { items: updatedItems }, { merge: true });
      setItems(updatedItems);
    } catch (err) {
      console.error('[useDepartments] save error:', err);
      throw err;
    }
  };

  // Sorted department name strings for dropdown consumers
  const departments = items
    .map((d) => d.name)
    .sort((a, b) => a.localeCompare(b));

  return { items, departments, loading, saveDepartments, refetch: fetchDepartments };
};

export default useDepartments;
